import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { useAuth } from '../../../hooks/useAuth';
import { getOrganization } from '../../../services/admin';
import type { RosterUploadProspect } from '../../../types/adminRosterUploads';
import {
  importerLabel,
  outcomePresentation,
  prospectDisplayName,
  prospectHref,
} from '../prospect-provenance/prospectProvenanceFormat';
import { EMPTY_CELL, formatCount, formatInstant } from './rosterUploadFormat';
import { useRosterUploadProspects } from './useRosterUploads';
import { AdminPanel } from '../AdminPage';

interface RosterUploadProspectsProps {
  uploadId: string;
  /**
   * The upload's organization. Every prospect an upload produced or matched was received there (the
   * server guarantees it), so it decides link-versus-switch and names the switch for every row.
   */
  organizationId: string;
  organizationName: string;
}

const OutcomeBadge = ({ outcome }: { outcome: string }) => {
  const { label, tone } = outcomePresentation(outcome);
  return <span className={`roster-uploads__status roster-uploads__status--${tone}`}>{label}</span>;
};

/**
 * Hook for the "switch organization, then open the prospect" action.
 *
 * A system admin sees an organization's prospects only by switching to it, through the same
 * `switchOrganization` the organization switcher uses. That needs the organization's name and
 * sport (the sport drives the prospect viewer's pitch set), so they are read fresh from the admin
 * organization endpoint rather than guessed. Only one switch can be in flight: it changes the whole
 * app's organization, so it is exclusive by nature. Pending is tracked per row, since the same
 * prospect can appear on several rows.
 *
 * The organization being viewed is snapshotted at click time. If the admin switches somewhere else
 * while the read is in flight, their later, deliberate choice wins and this one is dropped.
 */
const useSwitchAndOpenProspect = (organizationId: string) => {
  const { switchOrganization, effectiveOrganizationId } = useAuth();
  const navigate = useNavigate();
  const [pendingRowId, setPendingRowId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const inFlight = useRef(false);
  const latestOrganizationId = useRef(effectiveOrganizationId);

  useEffect(() => {
    latestOrganizationId.current = effectiveOrganizationId;
  }, [effectiveOrganizationId]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const open = async (row: RosterUploadProspect) => {
    if (inFlight.current) return;
    inFlight.current = true;
    const viewingAtClick = latestOrganizationId.current;
    setPendingRowId(row.id);
    setError(null);
    try {
      const organization = await getOrganization(organizationId);
      // Leaving the page before the answer came back means the admin changed their mind.
      if (!mounted.current) return;
      if (latestOrganizationId.current !== viewingAtClick) {
        setPendingRowId(null);
        return;
      }
      switchOrganization({
        id: organization.id,
        name: organization.name,
        sport: organization.sport ?? null,
      });
      navigate(prospectHref(row.prospectId));
    } catch (err: unknown) {
      if (!mounted.current) return;
      console.error('Failed to switch organization to open prospect:', err);
      setError("Could not switch to this prospect's organization. Please try again.");
      setPendingRowId(null);
    } finally {
      inFlight.current = false;
    }
  };

  return { open, pendingRowId, error };
};

interface ProspectNameCellProps {
  prospect: RosterUploadProspect;
  /** The admin is already viewing the upload's organization, so a plain link opens the prospect. */
  inViewedOrganization: boolean;
  organizationLabel: string;
  pending: boolean;
  /** Another switch is in flight. */
  blocked: boolean;
  onSwitchAndOpen: (prospect: RosterUploadProspect) => void;
}

const ProspectNameCell = ({
  prospect,
  inViewedOrganization,
  organizationLabel,
  pending,
  blocked,
  onSwitchAndOpen,
}: ProspectNameCellProps) => {
  const name = prospectDisplayName(prospect.firstName, prospect.lastName);

  if (prospect.prospectDeleted) {
    return (
      <span className="roster-uploads__stack">
        <span>{name}</span>
        <span className="roster-uploads__secondary">(deleted)</span>
      </span>
    );
  }

  if (inViewedOrganization) {
    return (
      <Link to={prospectHref(prospect.prospectId)} className="roster-uploads__row-link">
        <span className="roster-uploads__primary">{name}</span>
      </Link>
    );
  }

  // Opening it changes the organization the whole app is looking at, so the control says so in
  // visible text rather than leaving that to a surprise. While a switch is in flight the control is
  // aria-disabled rather than disabled: a disabled button drops the keyboard user's focus to the
  // body, and it would be left there if the switch fails.
  return (
    <button
      type="button"
      className="roster-upload__switch-open"
      onClick={() => {
        if (!blocked) onSwitchAndOpen(prospect);
      }}
      aria-disabled={blocked || undefined}
      aria-busy={pending || undefined}
    >
      <span className="roster-uploads__primary">{name}</span>
      <span className="roster-uploads__secondary">
        {pending ? 'Switching organization...' : `Switch to ${organizationLabel} to open`}
      </span>
    </button>
  );
};

/**
 * "Prospects from this upload": every prospect the upload created or matched, with a way to open
 * each one. Loads on its own, after the upload, so a failure here costs only this section.
 */
export const RosterUploadProspects = ({
  uploadId,
  organizationId,
  organizationName,
}: RosterUploadProspectsProps) => {
  const { state, reload } = useRosterUploadProspects(uploadId);
  const { effectiveOrganizationId } = useAuth();
  const { open, pendingRowId, error: switchError } = useSwitchAndOpenProspect(organizationId);

  const inViewedOrganization = Boolean(organizationId) && organizationId === effectiveOrganizationId;
  const organizationLabel = organizationName || 'its organization';

  const count = state.kind === 'loaded' ? ` (${formatCount(state.data.length)})` : '';

  return (
    <AdminPanel title={`Prospects from this upload${count}`} titleId="roster-upload-prospects-title">

      {state.kind === 'loading' && (
        <p className="admin-state" role="status">
          Loading prospects...
        </p>
      )}

      {state.kind === 'error' && (
        <div className="admin-error" role="alert">
          <span>{state.message}</span>
          <Button type="button" variant="secondary" onClick={reload}>
            Try again
          </Button>
        </div>
      )}

      {state.kind === 'loaded' && state.data.length === 0 && (
        <p className="admin-state" data-testid="roster-upload-prospects-empty">
          No prospects were imported from this upload, or it was imported before imports were
          tracked.
        </p>
      )}

      {switchError && (
        <p className="admin-error" role="alert">
          {switchError}
        </p>
      )}

      {state.kind === 'loaded' && state.data.length > 0 && (
        <div className="data-table-card">
          <div className="data-table-wrap">
            <table className="data-table" data-testid="roster-upload-prospects">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col" className="roster-uploads__numeric">
                    Grad year
                  </th>
                  <th scope="col">Position</th>
                  <th scope="col">Team</th>
                  <th scope="col">Outcome</th>
                  <th scope="col">Imported</th>
                </tr>
              </thead>
              <tbody>
                {state.data.map((prospect) => (
                  <tr key={prospect.id} data-testid={`roster-upload-prospect-${prospect.id}`}>
                    <td>
                      <ProspectNameCell
                        prospect={prospect}
                        inViewedOrganization={inViewedOrganization}
                        organizationLabel={organizationLabel}
                        pending={pendingRowId === prospect.id}
                        blocked={pendingRowId !== null}
                        onSwitchAndOpen={open}
                      />
                    </td>
                    <td className="roster-uploads__numeric">{prospect.gradYear ?? EMPTY_CELL}</td>
                    <td>{prospect.position ?? EMPTY_CELL}</td>
                    <td>{prospect.teamName ?? EMPTY_CELL}</td>
                    <td>
                      <OutcomeBadge outcome={prospect.outcome} />
                    </td>
                    <td>
                      <span className="roster-uploads__stack">
                        <span>{importerLabel(prospect.importedBy)}</span>
                        <time dateTime={prospect.importedAt} className="roster-uploads__secondary">
                          {formatInstant(prospect.importedAt)}
                        </time>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminPanel>
  );
};
